import React, { useMemo, useState } from 'react';
import { COGNITIVE_LANES, teamMembers, tasks } from '../mock';
import { Target, Layers, ChevronRight, Activity, Users, CheckCircle2, Network, Map } from 'lucide-react';
import { DESIGN } from '../components/DesignSystem';
import CognitiveMap from '../components/CognitiveMap';

// Get level color
const getLevelColor = (level) => {
  return DESIGN.levels[level]?.color || DESIGN.colors.textMuted;
};

// Get capacity color
const getCapacityColor = (percentage) => {
  if (percentage >= 90) return DESIGN.colors.error;
  if (percentage >= 70) return DESIGN.colors.amber;
  return '#10B981';
};

// Generate unique gradient for avatar
const getAvatarGradient = (name) => {
  const gradients = [
    'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
    'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
    'linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)',
    'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
    'linear-gradient(135deg, #89f7fe 0%, #66a6ff 100%)',
  ];
  const hash = name.split('').reduce((a, b) => a + b.charCodeAt(0), 0);
  return gradients[hash % gradients.length];
};

// Lane Card Component
const LaneCard = ({ lane, members, activeTasks }) => {
  const laneDesign = DESIGN.lanes[lane.id] || { color: lane.color, glow: `rgba(${lane.color}, 0.3)` };
  
  // Calculate lane health metrics
  const avgLoad = members.length > 0 
    ? Math.round(members.reduce((acc, m) => {
        const load = (m.current_load || Math.floor(Math.random() * m.max_task_load)) / m.max_task_load;
        return acc + load;
      }, 0) / members.length * 100)
    : 0;
  
  const laneHealth = avgLoad >= 80 ? 'overloaded' : avgLoad >= 60 ? 'elevated' : 'healthy';
  const healthColors = {
    healthy: '#10B981',
    elevated: DESIGN.colors.amber,
    overloaded: DESIGN.colors.error
  };

  return (
    <div 
      className="rounded-2xl overflow-hidden transition-all duration-300 hover:shadow-xl hover:-translate-y-1"
      style={{ 
        backgroundColor: DESIGN.colors.card,
        boxShadow: DESIGN.shadows.lg,
        borderLeft: `5px solid ${lane.color}`
      }}
      data-testid={`lane-card-${lane.id}`}
    >
      {/* Lane gradient tint background */}
      <div 
        className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{ background: `linear-gradient(135deg, ${lane.color} 0%, transparent 100%)` }}
      />
      
      {/* Lane Header */}
      <div 
        className="relative p-6"
        style={{ 
          background: `linear-gradient(135deg, ${lane.color}10 0%, transparent 100%)`
        }}
      >
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-4">
            <div 
              className="w-14 h-14 rounded-2xl flex items-center justify-center"
              style={{ 
                backgroundColor: `${lane.color}20`,
                boxShadow: `0 0 20px ${lane.color}30`
              }}
            >
              <div 
                className="w-6 h-6 rounded-full"
                style={{ 
                  backgroundColor: lane.color, 
                  boxShadow: `0 0 15px ${lane.color}60` 
                }}
              />
            </div>
            <div>
              <h3 
                className="text-xl font-black"
                style={{ color: DESIGN.colors.textPrimary }}
              >
                {lane.name}
              </h3>
              <div className="flex items-center gap-3 mt-1">
                <span 
                  className="flex items-center gap-1.5 text-sm font-semibold"
                  style={{ color: DESIGN.colors.textSecondary }}
                >
                  <Activity className="w-4 h-4" style={{ color: lane.color }} />
                  {activeTasks} active tasks
                </span>
              </div>
            </div>
          </div>
          
          {/* Lane Health Indicator */}
          <div 
            className="px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider"
            style={{ 
              backgroundColor: `${healthColors[laneHealth]}15`,
              color: healthColors[laneHealth],
              boxShadow: laneHealth === 'overloaded' ? `0 0 10px ${healthColors[laneHealth]}30` : 'none'
            }}
          >
            {laneHealth}
          </div>
        </div>
        
        {/* Quick Stats */}
        <div className="grid grid-cols-3 gap-3">
          <div 
            className="p-3 rounded-xl text-center"
            style={{ backgroundColor: `${lane.color}08` }}
          >
            <p className="text-2xl font-black" style={{ color: lane.color }}>{members.length}</p>
            <p className="text-[10px] font-bold uppercase tracking-wider" style={{ color: DESIGN.colors.textMuted }}>Members</p>
          </div>
          <div 
            className="p-3 rounded-xl text-center"
            style={{ backgroundColor: `${lane.color}08` }}
          >
            <p className="text-2xl font-black" style={{ color: lane.color }}>{activeTasks}</p>
            <p className="text-[10px] font-bold uppercase tracking-wider" style={{ color: DESIGN.colors.textMuted }}>Tasks</p>
          </div>
          <div 
            className="p-3 rounded-xl text-center"
            style={{ backgroundColor: `${healthColors[laneHealth]}08` }}
          >
            <p className="text-2xl font-black" style={{ color: healthColors[laneHealth] }}>{avgLoad}%</p>
            <p className="text-[10px] font-bold uppercase tracking-wider" style={{ color: DESIGN.colors.textMuted }}>Avg Load</p>
          </div>
        </div>
      </div>

      {/* Team Members */}
      <div className="p-6 pt-0">
        <div className="space-y-3">
          {members.length > 0 ? (
            members.slice(0, 4).map((member) => {
              const level = member.lane_proficiency[lane.id];
              const levelColor = getLevelColor(level);
              const currentLoad = member.current_load || Math.floor(Math.random() * member.max_task_load);
              const loadPercentage = Math.round((currentLoad / member.max_task_load) * 100);
              const capacityColor = getCapacityColor(loadPercentage);
              const onTimePercentage = 85 + Math.floor(Math.random() * 15);

              return (
                <div 
                  key={member.id} 
                  className="p-4 rounded-xl border transition-all hover:shadow-md hover:border-transparent group cursor-pointer"
                  style={{ 
                    borderColor: DESIGN.colors.border,
                    backgroundColor: DESIGN.colors.card
                  }}
                >
                  <div className="flex items-center gap-4">
                    {/* Avatar with capacity ring */}
                    <div className="relative flex-shrink-0">
                      <div 
                        className="w-12 h-12 rounded-full flex items-center justify-center text-sm font-bold text-white"
                        style={{ 
                          background: getAvatarGradient(member.name),
                          boxShadow: `0 0 0 3px ${DESIGN.colors.card}, 0 0 0 5px ${capacityColor}`
                        }}
                      >
                        {member.avatar}
                      </div>
                    </div>
                    
                    {/* Member Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="text-sm font-bold truncate" style={{ color: DESIGN.colors.textPrimary }}>
                          {member.name}
                        </p>
                        <span 
                          className="text-[10px] font-bold px-2 py-0.5 rounded-md flex-shrink-0"
                          style={{ backgroundColor: levelColor, color: 'white' }}
                        >
                          {level}
                        </span>
                      </div>
                      <p className="text-xs truncate" style={{ color: DESIGN.colors.textMuted }}>
                        {member.title}
                      </p>
                    </div>
                    
                    {/* Load Stats */}
                    <div className="flex items-center gap-4 flex-shrink-0">
                      <div className="text-right">
                        <p 
                          className="text-sm font-bold"
                          style={{ color: capacityColor }}
                        >
                          {currentLoad}/{member.max_task_load}
                        </p>
                        <p className="text-[10px]" style={{ color: DESIGN.colors.textMuted }}>Load</p>
                      </div>
                      <div className="text-right">
                        <p 
                          className="text-sm font-bold"
                          style={{ color: DESIGN.colors.primary }}
                        >
                          {onTimePercentage}%
                        </p>
                        <p className="text-[10px]" style={{ color: DESIGN.colors.textMuted }}>On-Time</p>
                      </div>
                    </div>
                    
                    <ChevronRight 
                      className="w-5 h-5 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" 
                      style={{ color: DESIGN.colors.textMuted }} 
                    />
                  </div>
                </div>
              );
            })
          ) : (
            <div 
              className="p-6 rounded-xl text-center"
              style={{ backgroundColor: DESIGN.colors.amberSubtle }}
            >
              <Layers className="w-8 h-8 mx-auto mb-2" style={{ color: '#92400E' }} />
              <p className="text-sm font-semibold" style={{ color: '#92400E' }}>
                No team members proficient in this lane
              </p>
              <p className="text-xs mt-1" style={{ color: '#B45309' }}>
                Consider cross-training or hiring
              </p>
            </div>
          )}
          
          {members.length > 4 && (
            <button 
              className="w-full py-3 rounded-xl text-sm font-semibold transition-all hover:shadow-md"
              style={{ 
                backgroundColor: `${lane.color}10`,
                color: lane.color
              }}
            >
              View all {members.length} members
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const Lanes = () => {
  const [activeTab, setActiveTab] = useState('lanes');
  
  // Calculate lane data
  const laneData = useMemo(() => {
    return COGNITIVE_LANES.map(lane => {
      const proficientMembers = teamMembers.filter(tm => 
        tm.lane_proficiency[lane.id]
      ).sort((a, b) => {
        const levelOrder = { 'L4': 4, 'L3': 3, 'L2': 2, 'L1': 1 };
        return levelOrder[b.lane_proficiency[lane.id]] - levelOrder[a.lane_proficiency[lane.id]];
      });

      const activeTasks = tasks.filter(t => t.primary_lane === lane.id).length;
      
      return {
        lane,
        members: proficientMembers,
        activeTasks
      };
    });
  }, []);

  // Calculate overview stats
  const totalMembers = teamMembers.length;
  const totalTasks = tasks.length;
  const healthyLanes = laneData.filter(d => {
    const avgLoad = d.members.length > 0 
      ? d.members.reduce((acc, m) => acc + (m.current_load || 0) / m.max_task_load, 0) / d.members.length
      : 0;
    return avgLoad < 0.6;
  }).length;

  return (
    <div className="space-y-8" data-testid="lanes-page">
      {/* Tab Navigation */}
      <div 
        className="flex items-center gap-2 p-1.5 rounded-2xl w-fit"
        style={{ backgroundColor: DESIGN.colors.card, boxShadow: DESIGN.shadows.md }}
      >
        <button
          onClick={() => setActiveTab('lanes')}
          className={`flex items-center gap-2 px-5 py-3 rounded-xl font-semibold text-sm transition-all`}
          style={{
            backgroundColor: activeTab === 'lanes' ? DESIGN.colors.primary : 'transparent',
            color: activeTab === 'lanes' ? 'white' : DESIGN.colors.textSecondary
          }}
          data-testid="tab-lanes"
        >
          <Layers className="w-4 h-4" />
          Lane Overview
        </button>
        <button
          onClick={() => setActiveTab('map')}
          className={`flex items-center gap-2 px-5 py-3 rounded-xl font-semibold text-sm transition-all`}
          style={{
            backgroundColor: activeTab === 'map' ? DESIGN.colors.primary : 'transparent',
            color: activeTab === 'map' ? 'white' : DESIGN.colors.textSecondary
          }}
          data-testid="tab-cognitive-map"
        >
          <Network className="w-4 h-4" />
          Cognitive Map
        </button>
      </div>
      
      {/* Conditional Content Based on Tab */}
      {activeTab === 'map' ? (
        <CognitiveMap />
      ) : (
        <>
          {/* Premium Header with Lane Color Spectrum */}
          <div className="relative">
        {/* Animated gradient background showing all lane colors */}
        <div 
          className="absolute inset-0 rounded-3xl opacity-60"
          style={{
            background: `linear-gradient(90deg, 
              ${COGNITIVE_LANES[0].color}15 0%, 
              ${COGNITIVE_LANES[1].color}15 10%, 
              ${COGNITIVE_LANES[2].color}15 20%, 
              ${COGNITIVE_LANES[3].color}15 30%, 
              ${COGNITIVE_LANES[4].color}15 40%, 
              ${COGNITIVE_LANES[5].color}15 50%, 
              ${COGNITIVE_LANES[6].color}15 60%, 
              ${COGNITIVE_LANES[7].color}15 70%, 
              ${COGNITIVE_LANES[8].color}15 80%, 
              ${COGNITIVE_LANES[9].color}15 100%
            )`
          }}
        />
        
        <div className="relative py-8 px-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-5">
              <div 
                className="w-16 h-16 rounded-2xl flex items-center justify-center relative overflow-hidden"
                style={{ 
                  background: `linear-gradient(135deg, ${DESIGN.colors.primary} 0%, #8B5CF6 50%, #3B82F6 100%)`,
                  boxShadow: '0 0 30px rgba(139, 92, 246, 0.4)'
                }}
              >
                <Target className="w-8 h-8 text-white relative z-10" />
              </div>
              <div>
                <h1 
                  className="text-4xl font-black tracking-tight"
                  style={{ color: DESIGN.colors.textPrimary }}
                >
                  COGNITIVE LANES
                </h1>
                <p className="text-base mt-1" style={{ color: DESIGN.colors.textSecondary }}>
                  The organizational intelligence framework
                </p>
              </div>
            </div>
            
            {/* Quick Stats */}
            <div className="flex items-center gap-6">
              <div className="text-center">
                <p className="text-3xl font-black" style={{ color: DESIGN.colors.textPrimary }}>{COGNITIVE_LANES.length}</p>
                <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: DESIGN.colors.textMuted }}>Lanes</p>
              </div>
              <div className="h-10 w-px" style={{ backgroundColor: DESIGN.colors.border }} />
              <div className="text-center">
                <p className="text-3xl font-black" style={{ color: DESIGN.colors.textPrimary }}>{totalMembers}</p>
                <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: DESIGN.colors.textMuted }}>Members</p>
              </div>
              <div className="h-10 w-px" style={{ backgroundColor: DESIGN.colors.border }} />
              <div className="text-center">
                <p className="text-3xl font-black" style={{ color: DESIGN.colors.success }}>{healthyLanes}</p>
                <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: DESIGN.colors.textMuted }}>Healthy</p>
              </div>
            </div>
          </div>
          
          {/* Lane Color Legend */}
          <div className="mt-6 flex items-center gap-1">
            {COGNITIVE_LANES.map(lane => (
              <div 
                key={lane.id}
                className="group relative flex-1 h-2 rounded-full cursor-pointer transition-all hover:h-4"
                style={{ backgroundColor: lane.color }}
                title={lane.name}
              >
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 rounded bg-slate-900 text-white text-[10px] whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity z-10">
                  {lane.name}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Lanes Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {laneData.map(({ lane, members, activeTasks }) => (
          <LaneCard 
            key={lane.id}
            lane={lane}
            members={members}
            activeTasks={activeTasks}
          />
        ))}
      </div>
      
      {/* Level Legend */}
      <div 
        className="p-6 rounded-2xl"
        style={{ 
          backgroundColor: DESIGN.colors.card,
          boxShadow: DESIGN.shadows.lg
        }}
      >
        <h3 className="text-sm font-bold uppercase tracking-wider mb-4" style={{ color: DESIGN.colors.textMuted }}>
          Cognitive Level System
        </h3>
        <div className="grid grid-cols-4 gap-4">
          {Object.entries(DESIGN.levels).map(([level, config]) => (
            <div 
              key={level}
              className="p-4 rounded-xl"
              style={{ backgroundColor: `${config.color}10` }}
            >
              <div className="flex items-center gap-3 mb-2">
                <span 
                  className="text-sm font-bold px-3 py-1 rounded-lg"
                  style={{ backgroundColor: config.color, color: 'white' }}
                >
                  {level}
                </span>
                <span className="text-sm font-semibold" style={{ color: config.color }}>
                  {config.label}
                </span>
              </div>
              <p className="text-xs" style={{ color: DESIGN.colors.textMuted }}>
                {config.name} level work
              </p>
            </div>
          ))}
        </div>
      </div>
        </>
      )}
    </div>
  );
};

export default Lanes;
